import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Trophy, ArrowLeft, Star, TrendingUp } from "lucide-react";
import { EnhancedLeaderboard } from "@/components/EnhancedLeaderboard";
import { EnhancedHeader } from "@/components/EnhancedHeader";
import { SEOHead } from "@/components/SEOHead";
import { StructuredData } from "@/components/StructuredData";
import { useAnalytics } from "@/hooks/useAnalytics";

export default function Leaderboard() {
  const { trackLeaderboardView } = useAnalytics();
  
  useEffect(() => {
    // Track leaderboard view
    trackLeaderboardView('global', 'this_week');
  }, [trackLeaderboardView]);

  // Sample leaderboard data for structured data
  const topTipsters = [
    { id: "1", name: "Elena Rodriguez", winRate: 82, roi: 31.7 },
    { id: "2", name: "Marcus Silva", winRate: 78, roi: 24.5 },
    { id: "3", name: "Alex Thompson", winRate: 75, roi: 22.1 },
    { id: "4", name: "James Mitchell", winRate: 71, roi: 18.2 },
    { id: "5", name: "Sofia Andersen", winRate: 69, roi: 16.8 }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="BetVerse Leaderboard – Top Sports Tipsters by ROI & Win Rate"
        description="Live rankings of top-performing sports tipsters on BetVerse. See the best betting analysts ranked by ROI, win rate, and consistency across football, basketball, tennis."
        canonical="https://betverse-global-picks.lovable.app/leaderboard"
      />
      
      <StructuredData 
        type="WebPage" 
        data={{
          title: "BetVerse Leaderboard",
          description: "Live rankings of top-performing tipsters based on ROI and win rate",
          url: "https://betverse-global-picks.lovable.app/leaderboard"
        }} 
      />
      
      <StructuredData
        type="ItemList"
        data={{
          title: "Top Tipsters Leaderboard",
          items: topTipsters
        }}
      />
      
      <StructuredData
        type="BreadcrumbList"
        data={{
          items: [
            { name: "Home", url: "https://betverse-global-picks.lovable.app/" },
            { name: "Leaderboard", url: "https://betverse-global-picks.lovable.app/leaderboard" }
          ]
        }}
      />
      
      <EnhancedHeader />
      
      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-primary/5 to-background">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <Button variant="ghost" size="sm" asChild className="mb-4">
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Link>
            </Button>
            
            <div className="flex items-center justify-center gap-3">
              <Trophy className="h-10 w-10 text-primary" />
              <h1 className="text-4xl md:text-5xl font-bold">
                Top Tipsters This Week
              </h1>
            </div>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Meet the top-performing tipsters ranked by ROI, win rate, and consistency. 
              Our leaderboard updates in real-time as tips are settled, giving you transparent 
              performance data to make informed decisions.
            </p>

            <div className="flex flex-wrap justify-center gap-6 pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">Live</div>
                <div className="text-sm text-muted-foreground">Rankings</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">Daily</div>
                <div className="text-sm text-muted-foreground">Updates</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">100%</div>
                <div className="text-sm text-muted-foreground">Transparent</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Rankings Work */}
      <section className="py-8 border-b">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <Card className="p-6 bg-muted/30">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Star className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold mb-2">How Our Leaderboard Works</h2>
                  <p className="text-muted-foreground">
                    Tipsters are ranked based on three key metrics: <strong>Return on Investment (ROI)</strong>, 
                    <strong> Win Rate</strong>, and <strong>Total Tips</strong>. We track performance across 
                    daily, weekly, and monthly periods. All statistics are verified and updated automatically 
                    as matches conclude. Filter by sport or region to find specialists in your area of interest.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Leaderboard */}
      <section className="py-12">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <EnhancedLeaderboard 
              title="Global Rankings"
              subtitle="Filter by period, sport, and region to find the best tipsters"
              showFilters={true}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">Want to Join the Ranks?</h2>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Become a verified tipster and showcase your sports analytics expertise to thousands of users.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/become-tipster">
                <TrendingUp className="h-5 w-5 mr-2" />
                Become a Tipster
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/tips">
                Browse Premium Tips
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container">
          <div className="text-center text-sm text-muted-foreground">
            <p>Leaderboard updates every 24 hours • Last updated: {new Date().toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
